import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'

export type AuthResult = {
  access_token: string
  user: { id: string; email: string; name: string; role: string }
}

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) {
    return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  }
  return path.startsWith('/') ? path : `/${path}`
}

async function requestJson(method: string, path: string, body?: unknown) {
  const token = Cookies.get('token')
  const url = buildUrl(path)
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = await res.json().catch(() => null)
    let msg: string | undefined = undefined
    if (payload) {
      if (typeof payload === 'string') msg = payload
      else if (typeof payload.message === 'string') msg = payload.message
      else if (typeof payload.error === 'string') msg = payload.error
      else if (typeof payload.error === 'object' && payload.error !== null) {
        if (Array.isArray(payload.error.details) && payload.error.details.length > 0) {
          msg = payload.error.details.map((d: { field?: string; message?: string }) => d.message || d.field).join('. ')
        } else {
          msg = (payload.error.message as string) || (payload.error.code as string)
        }
      } else if (typeof payload.code === 'string') msg = payload.code
    }
    msg = msg || res.statusText || 'Request failed'
    throw new Error(msg)
  }

  return res.json()
}

export async function login(payload: { email: string; password: string }): Promise<AuthResult> {
  return requestJson('POST', '/api/auth/login', payload)
}

export async function register(payload: {
  name: string
  email: string
  password: string
  confirm_password?: string
}): Promise<AuthResult> {
  return requestJson('POST', '/api/auth/register', payload)
}

export type MeProfile = { id: string; email: string; name: string; role: string; jobTitle?: string; emailSignature?: string }

export async function getMe(): Promise<MeProfile> {
  return requestJson('GET', '/api/auth/me')
}

export async function updateProfile(payload: { name?: string; job_title?: string; email_signature?: string }): Promise<MeProfile> {
  return requestJson('PATCH', '/api/auth/me', payload)
}

export async function changePassword(payload: { current_password: string; password: string; confirm_password: string }): Promise<{ id: string; email: string; name: string; role: string }> {
  return requestJson('PATCH', '/api/auth/me', payload)
}

export async function resetPassword(payload: { email: string; password: string; confirm_password: string }): Promise<void> {
  return requestJson('POST', '/api/auth/reset-password', payload)
}

// Full-page redirect, not a fetch — the browser must land on Google's own
// consent screen, which an XHR/fetch response body cannot do.
export const googleSignInStartUrl = buildUrl('/api/auth/google/start')

export type GoogleSessionResult = {
  access_token: string
  user: { id: string; email: string; name: string }
  isNewUser: boolean
}

// Exchanges the one-time code from the /login/google/callback redirect for a
// real session. See apps/api's auth/google/handoff-repository.ts for why the
// callback can't hand back the session directly.
export async function exchangeGoogleSession(code: string): Promise<GoogleSessionResult> {
  return requestJson('POST', '/api/auth/google/session', { code })
}

// Full-page redirect, not a fetch — mirrors googleSignInStartUrl above.
export const microsoftSignInStartUrl = buildUrl('/api/auth/microsoft/start')

export type MicrosoftSessionResult = {
  access_token: string
  user: { id: string; email: string; name: string }
  isNewUser: boolean
}

// Exchanges the one-time code from the /login/microsoft/callback redirect for
// a real session. Mirrors exchangeGoogleSession above.
export async function exchangeMicrosoftSession(code: string): Promise<MicrosoftSessionResult> {
  return requestJson('POST', '/api/auth/microsoft/session', { code })
}
